import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, Eye, Pencil, Hash, ArrowRight, ShieldAlert } from 'lucide-react';

/**
 * DuplicatePoWarningModal
 * ----------------------------------------------------------------
 * Shows when the backend (or live check) reports that a (customer,
 * normalized PO) combination is already in use. The flow honours
 * the dispatched-order rule: no direct Edit on dispatched orders.
 *
 * Props:
 *   open                  : boolean
 *   onOpenChange(open)    : closer
 *   existingOrder         : { id, order_reference, customer_name, po_number,
 *                             status, dispatch_date, is_dispatched, ... }
 *   poNumber              : the PO the operator just entered
 *   onChangePoNumber()    : called when "Change PO Number" is clicked
 *   onProceedAnyway()     : called when the user wants to create the duplicate
 *                          regardless. Implementation should resend with
 *                          acknowledge_duplicate_po=true.
 *   isEditingCurrent      : whether the current screen is the same order being
 *                          shown — disables the View/Edit buttons to avoid
 *                          recursion.
 */
export default function DuplicatePoWarningModal({
  open,
  onOpenChange,
  existingOrder,
  poNumber,
  onChangePoNumber,
  onProceedAnyway,
  isEditingCurrent = false,
}) {
  const navigate = useNavigate();
  if (!existingOrder) return null;

  const isDispatched = existingOrder.is_dispatched || existingOrder.status === 'dispatched';

  const viewExisting = () => {
    onOpenChange(false);
    navigate(`/planner?focus=${existingOrder.id}`);
  };
  const editExisting = () => {
    onOpenChange(false);
    navigate(`/sales-order/edit/${existingOrder.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto"
        data-testid="duplicate-po-warning-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            PO number already used for this customer
          </DialogTitle>
          <DialogDescription className="pt-2 text-zinc-700">
            This PO number has already been used for this customer. This may mean the
            customer is asking to modify the previous order, or it may be a new order
            using the same reference.
          </DialogDescription>
        </DialogHeader>

        {/* Existing-order summary card */}
        <div className="rounded-lg border bg-zinc-50 p-3 space-y-1.5 text-sm min-w-0">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wide">
            <Hash className="w-3 h-3" /> Existing order
          </div>
          <div className="grid grid-cols-3 gap-2 min-w-0">
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-400 uppercase">Reference</div>
              <div className="font-semibold text-zinc-900 truncate">{existingOrder.order_reference || existingOrder.dispatch_number || '—'}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-400 uppercase">Customer</div>
              <div className="font-medium text-zinc-900 truncate" title={existingOrder.customer_name || ''}>{existingOrder.customer_name || '—'}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-400 uppercase">PO number</div>
              <div className="font-mono text-zinc-900 truncate" title={existingOrder.po_number || ''}>{existingOrder.po_number || '—'}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-400 uppercase">Order date</div>
              <div className="text-zinc-900 truncate">
                {existingOrder.po_date || (existingOrder.created_at ? new Date(existingOrder.created_at).toLocaleDateString('en-IE') : '—')}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-400 uppercase">Status</div>
              <Badge variant={isDispatched ? 'destructive' : 'secondary'} className="text-[10px] capitalize">
                {existingOrder.status || '—'}
              </Badge>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-400 uppercase">Dispatch date</div>
              <div className="text-zinc-900 truncate">{existingOrder.dispatch_date || '—'}</div>
            </div>
          </div>
        </div>

        {/* Dispatched-order stronger warning */}
        {isDispatched && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2" data-testid="duplicate-po-dispatched-warning">
            <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              This PO number has already been used on a <strong>dispatched order</strong>.
              Editing a dispatched order may affect dispatch records, traceability,
              customer documentation, and audit history.
            </p>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={viewExisting}
            disabled={isEditingCurrent}
            className="gap-1.5"
            data-testid="btn-view-existing-order"
          >
            <Eye className="w-4 h-4" />
            {isDispatched ? 'View Dispatched Order' : 'View Existing Order'}
          </Button>
          {!isDispatched && (
            <Button
              variant="outline"
              onClick={editExisting}
              disabled={isEditingCurrent}
              className="gap-1.5"
              data-testid="btn-edit-existing-order"
            >
              <Pencil className="w-4 h-4" />
              Edit Existing Order
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => { onOpenChange(false); onChangePoNumber?.(); }}
            className="gap-1.5"
            data-testid="btn-change-po-number"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Change PO Number
          </Button>
          <Button
            variant="default"
            onClick={() => { onProceedAnyway?.(); }}
            className="gap-1.5 bg-amber-600 hover:bg-amber-700"
            data-testid="btn-proceed-with-duplicate"
          >
            <AlertTriangle className="w-4 h-4" />
            Create New Order Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
